import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { departmentColorFrom, departmentColorMap } from '@/utils/departmentColors';
import { countDepartments } from '@/utils/departments';
import { fetchProfilesByIds } from '@/utils/fetchProfilesByIds';

export interface EventStats {
  averageAge: number;
  averageHoursPerVolunteer: number;
  totalHours: number;
  totalVolunteers: number;
}

export interface ChartPoint {
  date: string;
  hours: number;
}

export interface VolunteersChartPoint {
  date: string;
  count: number;
}

export interface PieDataItem {
  value: number;
  text: string;
  color: string;
}

const EMPTY_EVENT_STATS: EventStats = {
  averageAge: 0,
  averageHoursPerVolunteer: 0,
  totalHours: 0,
  totalVolunteers: 0,
};

export function useEventStats(selectedEventId: string | null, activeTab: string) {
  const selectedEventIdRef = useRef(selectedEventId);
  selectedEventIdRef.current = selectedEventId;

  const [eventStats, setEventStats] = useState<EventStats>({
    averageAge: 0,
    averageHoursPerVolunteer: 0,
    totalHours: 0,
    totalVolunteers: 0,
  });
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [volunteersChartData, setVolunteersChartData] = useState<VolunteersChartPoint[]>([]);
  const [pieChartData, setPieChartData] = useState<PieDataItem[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    setEventStats(EMPTY_EVENT_STATS);
    setChartData([]);
    setVolunteersChartData([]);
    setPieChartData([]);
  }, [selectedEventId]);

  const fetchEventStats = useCallback(async () => {
    const requestedEventId = selectedEventId;
    if (!requestedEventId) {
      setEventStats(EMPTY_EVENT_STATS);
      setChartData([]);
      setVolunteersChartData([]);
      setPieChartData([]);
      return;
    }
    setLoadingStats(true);
    try {
      const { data: vols, error: volErr } = await supabase
        .from('volunteers')
        .select('id, hours, volunteer_id, is_active')
        .eq('event_id', requestedEventId);

      if (requestedEventId !== selectedEventIdRef.current) return;

      if (volErr || !vols || vols.length === 0) {
        setEventStats(EMPTY_EVENT_STATS);
        setChartData([]);
        setVolunteersChartData([]);
        setPieChartData([]);
        return;
      }

      const active = vols.filter((v: { is_active: boolean }) => v.is_active !== false);
      const totalVolunteers = active.length;
      const totalHours = vols.reduce((s: number, v: { hours?: number }) => s + (v.hours || 0), 0);
      const avgHours = totalVolunteers > 0 ? totalHours / totalVolunteers : 0;

      const activeIds = active.map((v: { volunteer_id: string }) => v.volunteer_id).filter(Boolean);
      let avgAge = 0;
      if (activeIds.length > 0) {
        const { profiles } = await fetchProfilesByIds<{ id: string; age?: number }>(
          activeIds,
          'id, age',
          'useEventStats',
        );
        if (requestedEventId !== selectedEventIdRef.current) return;
        {
          const withAge = profiles.filter((p: { age?: number }) => p.age && Number(p.age) > 0 && Number(p.age) < 120);
          const totalAge = withAge.reduce((s: number, p: { age?: number }) => s + Number(p.age || 0), 0);
          avgAge = withAge.length > 0 ? Math.round((totalAge / withAge.length) * 10) / 10 : 0;
        }
      }
      setEventStats({ averageAge: avgAge, averageHoursPerVolunteer: avgHours, totalHours, totalVolunteers });

      const { data: activities } = await supabase
        .from('accepted_volunteer_hours')
        .select('start_time, hours')
        .eq('event_id', requestedEventId)
        .order('start_time', { ascending: true });
      if (requestedEventId !== selectedEventIdRef.current) return;
      if (activities && activities.length > 0) {
        const daily: Record<string, number> = {};
        activities.forEach((a: { start_time: string; hours?: number }) => {
          const d = new Date(a.start_time).toISOString().split('T')[0];
          daily[d] = (daily[d] || 0) + (a.hours || 0);
        });
        let cum = 0;
        const sorted = Object.entries(daily)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, h]) => {
            cum += h;
            return { date, hours: cum };
          });
        setChartData(sorted);
      } else {
        setChartData([]);
      }

      const { data: volsChart } = await supabase
        .from('volunteers')
        .select('created_at')
        .eq('event_id', requestedEventId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (requestedEventId !== selectedEventIdRef.current) return;
      if (volsChart && volsChart.length > 0) {
        const daily: Record<string, number> = {};
        volsChart.forEach((v: { created_at: string }) => {
          const d = new Date(v.created_at).toISOString().split('T')[0];
          daily[d] = (daily[d] || 0) + 1;
        });
        let cum = 0;
        const sorted = Object.entries(daily)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, c]) => {
            cum += c;
            return { date, count: cum };
          });
        setVolunteersChartData(sorted);
      } else {
        setVolunteersChartData([]);
      }

      const { data: deptData } = await supabase
        .from('volunteers')
        .select('department')
        .eq('event_id', requestedEventId)
        .eq('is_active', true)
        .not('department', 'is', null);
      if (requestedEventId !== selectedEventIdRef.current) return;
      if (deptData && deptData.length > 0) {
        // countDepartments applies the same comma split and case-insensitive grouping as
        // the task policies and department-chat membership. Counting raw strings here used
        // to give 'Logistics' and 'logistics' separate slices -- and, since the colour
        // helper normalizes, two slices in the SAME colour.
        const counts = countDepartments(
          (deptData as { department: string | null }[]).map((v) => v.department)
        );
        // Colours are keyed by department name rather than by position in this list, so a
        // department keeps its colour as the list changes, and usually matches the
        // Communication page's card for it -- see departmentColorMap on why "usually".
        const colorMap = departmentColorMap(counts.map((c) => c.label));
        const pie = counts.map((c) => ({
          value: c.value,
          text: c.label,
          color: departmentColorFrom(colorMap, c.label),
        }));
        setPieChartData(pie);
      } else {
        setPieChartData([]);
      }
    } catch (err) {
      console.error('Error fetching event stats:', err);
    } finally {
      if (requestedEventId === selectedEventIdRef.current) setLoadingStats(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (activeTab === 'stats' && selectedEventId) fetchEventStats();
  }, [activeTab, selectedEventId, fetchEventStats]);

  return {
    eventStats,
    chartData,
    volunteersChartData,
    pieChartData,
    loadingStats,
    fetchEventStats,
  };
}
